import { useCallback, useState } from 'react';

import produce from 'immer';

import { deleteRequest, post, put } from 'helpers/api';
import { findEmployerRole } from 'bundles/backoffice/contacts/contact_role_employment';

import type { Contact, ContactRole, Visibility } from 'bundles/backoffice/contacts/types';

type KeysByType = { [key: string]: string };
interface ParentsByType {
  commissions?: { contact: Contact }[];
  contactRoles?: ContactRole[];
  deposits?: { payer: Contact }[];
  payables?: { payee: Contact }[];
}

export function replaceContact(
  contact: Contact,
  parentsByType: ParentsByType,
  keysByType: KeysByType = {},
): ParentsByType {
  return produce(parentsByType, (draftParentsByType) => {
    forEachContact(draftParentsByType, keysByType, (otherContact, parent, contactKey) => {
      if (otherContact.id === contact.id) {
        parent[contactKey] = contact;
      }
    });
  });
}

function forEachContact(
  parentsByType: ParentsByType,
  keysByType: KeysByType,
  callback: (contact: Contact, parent: object, contactKey: string) => boolean | void
) {
  for (const type in parentsByType) {
    const key = keysByType[type] || 'contact';

    for (const parent of parentsByType[type]) {
      const contact = parent[key];
      if (isContact(contact)) {
        if (callback(contact, parent, key) === false) {
          return;
        }
      }
    }
  }
}

function isContact(entity: { contactType?: string }) {
  // These functions may be used with things like Backoffice::Payable, which has a polymorphic payer
  // So, need to verify that things really are contacts
  return entity && entity.contactType;
}

interface Record {
  id: number;
  type: string ;
}

interface CreateContactRoleArgs {
  contact: Contact;
  employer?: Contact | null;
  recordId?: number,
  recordType?: string,
  role: string;
  sharedAttributes?: string[];
  visibility?: Visibility;
}

interface CreateContactRoleBody {
  contactId: number;
  employerRecordId?: number;
  recordId: number;
  recordType: string;
  role: string;
  sharedAttributes?: string[] | null;
  visibility?: Visibility;
}

interface UseContactRolesOptions {
  visibility?: Visibility;
}

export function useContactRoles(
  initialContactRoles: ContactRole[],
  record: Record,
  options: UseContactRolesOptions = {}
) {
  const [contactRoles, setContactRoles] = useState(initialContactRoles);

  const createContactRole = useCallback(async (
    { contact, employer, recordId, recordType, role, sharedAttributes, visibility }: CreateContactRoleArgs
  ) => {
    const body: CreateContactRoleBody = {
      contactId: contact.id,
      recordId: recordId || record.id,
      recordType: recordType || record.type,
      role,
      sharedAttributes,
      visibility: visibility || options.visibility,
    };

    const newContactRoles: ContactRole[] = [];
    if (employer) {
      let employerRecord = findEmployerRole(employer, role, contactRoles);
      if (!employerRecord) {
        employerRecord = await post('/crm/contacts_records', {
          ...body,
          contactId: employer.id,
          sharedAttributes: employer.sharedAttributes,
        }) as ContactRole;

        newContactRoles.push(employerRecord);
      }

      body.employerRecordId = employerRecord.id;
    }

    const contactRole = await post('/crm/contacts_records', body);
    newContactRoles.unshift(contactRole);

    setContactRoles(contactRoles => [...contactRoles, ...newContactRoles]);
  }, [contactRoles]);

  const deleteContactRole = useCallback(async (contactRole: ContactRole) => {
    await deleteRequest(contactRole.path);
    setContactRoles(contactRoles => contactRoles.filter(role => role.id !== contactRole.id));
  }, []);

  const updateContactRole = useCallback(async (contactRole: ContactRole) => {
    await put(contactRole.path, { ...contactRole, contactId: contactRole.contact.id });
    setContactRoles(contactRoles => contactRoles.map(role => role.id === contactRole.id ? contactRole : role));
  }, []);

  return { contactRoles, createContactRole, deleteContactRole, updateContactRole };
}
